import React, { useState } from "react";
import { ChevronDown, Camera, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { rideAPI, uploadAPI } from "../services/api";
import PhotoCaptureButton from "./PhotoCaptureButton";
import SubmitCountdown from "./SubmitCountdown";

const VehicleDetailsCard = ({
  booking = {},
  driverInfo = {},
  isExpanded,
  onToggle,
  onDriverInfoChange,
  onPhotoSelect,
  vehiclePhotoUrl,
  rideId,
  recordId,
  locked = false,
  onSubmitSuccess = () => {},
}) => {
  const [vehicleModel, setVehicleModel] = useState(
    driverInfo.vehicle_model ||
      driverInfo["Vehicle Model"] ||
      booking.vehicle_model ||
      ""
  );
  const [vehicleColour, setVehicleColour] = useState(
    driverInfo.vehicle_color ||
      driverInfo["Vehicle Color"] ||
      booking.vehicle_color ||
      ""
  );
  const [vehiclePlateNumber, setVehiclePlateNumber] = useState(
    driverInfo.vehicle_plate_number ||
      driverInfo["Vehicle Plate Number"] ||
      booking.vehicle_plate_number ||
      ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFileObject, setSelectedFileObject] = useState(null);
  const fileInputRef = React.useRef(null);

  const handlePhotoCapture = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    let file;

    // Handle both event object (from file input) and File object (from PhotoCaptureButton)
    if (e instanceof File) {
      file = e;
    } else if (e?.target?.files?.[0]) {
      file = e.target.files[0];
    }

    if (file) {
      setSelectedFileObject(file);
      onPhotoSelect(file);
    }
  };

  const handleSubmitVehicleInfo = async () => {
    try {
      setIsSubmitting(true);

      let vehiclePhotoUrlUpload = null;

      // Upload vehicle photo if file was selected
      if (selectedFileObject) {
        if (
          !["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(
            selectedFileObject.type
          )
        ) {
          toast.error("Please select a JPG, JPEG, PNG, or WebP image.");
          return;
        }
        vehiclePhotoUrlUpload = await uploadAPI.uploadPhoto(
          selectedFileObject,
          rideId,
          "vehicle_photo.jpg"
        );
      } else if (vehiclePhotoUrl && vehiclePhotoUrl.startsWith("http")) {
        // If vehiclePhotoUrl is already an HTTP URL, use it directly
        vehiclePhotoUrlUpload = vehiclePhotoUrl;
      }

      const payload = {
        vehicle_model: vehicleModel,
        vehicle_color: vehicleColour,
        vehicle_plate_number: vehiclePlateNumber,
        ...(vehiclePhotoUrlUpload && {
          vehicle_photo_url: vehiclePhotoUrlUpload,
        }),
      };

      const response = await rideAPI.submitVehicleInfo(
        rideId,
        recordId,
        payload
      );

      if (response.success) {
        toast.success("Vehicle info submitted successfully!");
        onSubmitSuccess(response);
      }
    } catch (err) {
      console.error("Failed to submit vehicle info:", err);
      if (err.message && err.message.includes("409")) {
        toast.error("Vehicle info already submitted (write-once)");
      } else {
        toast.error(err.message || "Failed to submit vehicle info");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-all duration-300">
      {/* Collapsed Summary */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="text-left">
          <p className="font-medium text-gray-900">Vehicle Details</p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 px-4 py-4 space-y-4 bg-gray-50">
          {/* Vehicle Model */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
              Vehicle Model
            </label>
            <input
              type="text"
              value={vehicleModel}
              onChange={(e) => {
                setVehicleModel(e.target.value);
                onDriverInfoChange({ vehicle_model: e.target.value });
              }}
              placeholder="Enter vehicle model"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={locked}
            />
          </div>

          {/* Vehicle Color */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
              Vehicle Color
            </label>
            <input
              type="text"
              value={vehicleColour}
              onChange={(e) => {
                setVehicleColour(e.target.value);
                onDriverInfoChange({ vehicle_color: e.target.value });
              }}
              placeholder="Enter vehicle color"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={locked}
            />
          </div>

          {/* Vehicle Plate Number */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
              Vehicle Plate Number
            </label>
            <input
              type="text"
              value={vehiclePlateNumber}
              onChange={(e) => {
                setVehiclePlateNumber(e.target.value);
                onDriverInfoChange({ vehicle_plate_number: e.target.value });
              }}
              placeholder="Enter vehicle plate number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={locked}
            />
          </div>

          {/* Vehicle Photo */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
              Vehicle Photo
            </label>
            <div className="flex items-center gap-3">
              {vehiclePhotoUrl ? (
                <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-green-400">
                  <img
                    src={vehiclePhotoUrl}
                    alt="Vehicle photo"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-100">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <PhotoCaptureButton
                onPhotoSelect={handleFileSelect}
                disabled={locked}
                hasPhoto={!!vehiclePhotoUrl}
                buttonText="Capture Photo"
                retakeText="Retake Photo"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                disabled={locked}
              />
            </div>
          </div>

          {/* Submit Button */}
          {!locked && (
            <button
              onClick={handleSubmitVehicleInfo}
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <SubmitCountdown />
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Submit Vehicle Info
                </>
              )}
            </button>
          )}
          {locked && (
            <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-xs font-medium text-green-700">
                ✓ Vehicle info submitted
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleDetailsCard;
